import React, { forwardRef, useId } from "react";

export interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
  label?: string;
  error?: string;
  helperText?: string;
  size?: "sm" | "md" | "lg";
  fullWidth?: boolean;
  variant?: "default" | "filled" | "ghost";
  resize?: "none" | "vertical" | "horizontal" | "both";
}

/**
 * Arc-style Textarea component
 * 
 * Features:
 * - Multiple variants: default, filled, ghost
 * - Multiple sizes: sm, md, lg
 * - Label and helper text support
 * - Error state
 * - Configurable resize behavior
 * - Smooth focus transitions
 */
const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      helperText,
      size = "md",
      fullWidth = true,
      variant = "default",
      resize = "vertical",
      className = "",
      disabled = false,
      required = false,
      id,
      rows = 4,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const textareaId = id || generatedId;
    const hasError = !!error;

    // Size styles
    const sizeStyles = {
      sm: {
        textarea: "py-2 px-3 text-sm",
        label: "text-xs mb-1.5",
      },
      md: {
        textarea: "py-2.5 px-4 text-sm",
        label: "text-sm mb-2",
      },
      lg: {
        textarea: "py-3.5 px-5 text-base",
        label: "text-sm mb-2",
      },
    };

    const currentSize = sizeStyles[size];

    // Variant styles - using ring for focus with accent color
    const variantStyles = {
      default: `
        bg-surface-elevated
        border border-arc-border
        hover:border-arc-border-strong
        focus:border-transparent focus:ring-2 focus:ring-accent
      `,
      filled: `
        bg-surface-secondary
        border border-transparent
        hover:bg-surface-tertiary
        focus:bg-surface-elevated focus:ring-2 focus:ring-accent
      `,
      ghost: `
        bg-transparent
        border border-transparent
        hover:bg-surface-secondary
        focus:bg-surface-secondary focus:ring-2 focus:ring-accent
      `,
    };

    // Resize styles
    const resizeStyles = {
      none: "resize-none",
      vertical: "resize-y",
      horizontal: "resize-x",
      both: "resize",
    };

    // Base textarea styles - Arc-style design
    const textareaBaseStyles = `
      w-full
      rounded-arc-md
      font-medium
      text-content-primary
      placeholder:text-content-tertiary placeholder:font-normal
      transition-all duration-arc-normal ease-arc
      outline-none
      disabled:bg-surface-tertiary disabled:text-content-tertiary disabled:cursor-not-allowed disabled:border-arc-border
    `;

    const errorStyles = hasError
      ? "border-error hover:border-error focus:border-transparent focus:ring-2 focus:ring-error-main"
      : "";

    return (
      <div className={`${fullWidth ? "w-full" : ""} ${className}`}>
        {label && (
          <label
            htmlFor={textareaId}
            className={`block font-semibold text-content-primary ${currentSize.label}`}
          >
            {label}
            {required && <span className="text-error ml-1">*</span>}
          </label>
        )}

        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          required={required}
          rows={rows}
          className={`
            ${textareaBaseStyles}
            ${variantStyles[variant]}
            ${currentSize.textarea}
            ${resizeStyles[resize]}
            ${errorStyles}
          `}
          {...props}
        />

        {(error || helperText) && (
          <p
            className={`mt-1.5 text-xs ${
              hasError ? "text-error" : "text-content-tertiary"
            }`}
          >
            {error || helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = "Textarea";

export default Textarea;
